import React, { useEffect, useCallback, useMemo, useState, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import Form, { Field } from '@atlaskit/form';
import { Grid, GridColumn } from '@atlaskit/page';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import isValidEmailAddress from '@atlassian/jira-common-is-valid-email-address/src/index.tsx';
import fireErrorAnalytics, {
	trackFetchErrors,
} from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { isValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagService, toFlagId } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import useCheckboxRecaptcha from '@atlassian/jira-google-recaptcha/src/ui/checkbox-recaptcha/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useSendMailToAdmin } from '../../services/send-mail-to-administrator/index.tsx';
import type { FormData } from '../../services/types.tsx';
import {
	CONTACT_ADMINISTRATOR_ID,
	SUBJECT_LENGTH,
	DETAILS_LENGTH,
	CONTACT_ADMINT_PACKAGE_NAME,
} from './constants.tsx';
import ErrorComponent from './error/index.tsx';
import messages from './messages.tsx';
import type { ContactAdminFormType } from './types.tsx';

const ERROR_FLAG_ID = toFlagId('CONTACT_ADMIN_SUBMIT_ERROR');
const SUCCESS_FLAG_ID = toFlagId('CONTACT_ADMIN_SUBMIT_SUCESS');

const ContactAdminForm = (props: ContactAdminFormType) => {
	const { formatMessage } = useIntl();
	const { to, replyTo, updatePageCrash, reCaptchaSharedKey, infoBannerMessage } = props;
	const { loading, error: postApiError, data, sendMailToAdmin } = useSendMailToAdmin();
	const config = useMemo(() => ({ sitekey: reCaptchaSharedKey || '' }), [reCaptchaSharedKey]);
	const shouldRecaptchaWork = useMemo(() => reCaptchaSharedKey !== null, [reCaptchaSharedKey]);
	const { showFlag } = useFlagService();
	const { push } = useRouterActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isSubmitEnabled, updateSubmitState] = useState(!shouldRecaptchaWork);
	const onVerify = useCallback(() => {
		updateSubmitState(true);
	}, []);
	const onLoad = useCallback(() => {
		// can be relied upon to see if script was loaded in UI or not
		// no analytics needed
	}, []);
	const onRender = useCallback(() => {
		// TODO: analytics to inform google RECAPTCHA was successfully rendered in UI
		// no analytics needed
	}, []);

	const onError = useCallback(() => {
		updatePageCrash(true);
		fireErrorAnalytics({
			error: new Error('failed to verify recaptcha validation'),
			meta: {
				id: 'contactAdminRecaptchaErrorCallback',
				packageName: CONTACT_ADMINT_PACKAGE_NAME,
				teamName: 'jira-notifications-platform',
			},
			// Skipping sentry since there is no action for us to do.
			skipSentry: true,
		});
	}, [updatePageCrash]);
	// force user to revalidate the recaptcha if the user Fails
	const onExpire = useCallback(() => {
		if (shouldRecaptchaWork) {
			updateSubmitState(false);
		}
	}, [shouldRecaptchaWork]);
	const onScriptError = useCallback(
		(error: Error | undefined) => {
			updatePageCrash(true);
			fireErrorAnalytics({
				error,
				meta: {
					id: 'contactAdminRecaptchaScriptLoadFailed',
					packageName: CONTACT_ADMINT_PACKAGE_NAME,
					teamName: 'jira-notifications-platform',
				},
			});
		},
		[updatePageCrash],
	);

	const callbacks = useMemo(
		() => ({
			onVerify,
			onLoad,
			onError,
			onExpire,
			onRender,
			onScriptError,
		}),
		[onVerify, onLoad, onError, onExpire, onRender, onScriptError],
	);
	const {
		recaptchaComponent,
		token: captchaToken,
		reset,
	} = useCheckboxRecaptcha(config, callbacks);

	const onSuccess = useCallback(() => {
		fireTrackAnalytics(
			createAnalyticsEvent({
				action: 'submit',
			}),
			'contactAdmin submitted',
			{
				isRecaptchaEnabled: true,
				isUserLoggedIn: to !== '',
				containsContactAdminMessage: infoBannerMessage !== '',
			},
		);
		showFlag({
			id: SUCCESS_FLAG_ID,
			type: 'success',
			testId: 'admin-pages-screens-page.ui.contact-admin-form.success',
			title: messages.successMessageMailSent,
		});
		setTimeout(() => {
			push('/');
		}, 1000);
	}, [createAnalyticsEvent, to, infoBannerMessage, showFlag, push]);
	const onFailure = useCallback(() => {
		if (shouldRecaptchaWork) {
			updateSubmitState(false);
			reset();
		}
		if (postApiError) {
			trackFetchErrors({
				error: postApiError,
				id: 'sendContactAdminMail',
				packageName: CONTACT_ADMINT_PACKAGE_NAME,
			});
		}
		if (postApiError && isValidationError(postApiError)) {
			// @ts-expect-error - TS2339 - Property 'errors' does not exist on type 'Error'.
			const { errors, message } = postApiError;

			// @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
			const errorMessages = errors.map((item) => item.error.toString());
			if (errorMessages.length >= 1) {
				// @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
				errorMessages.forEach((item) =>
					showFlag({
						id: ERROR_FLAG_ID,
						type: 'error',
						testId: 'admin-pages-screens-page.ui.contact-admin-form.error',
						title: {
							id: 'admin-pages-contact-administrator.contact-admin-form.server-error-message-could-not-send-mail',
							defaultMessage: item,
							description: 'Mail could not be sent',
						},
					}),
				);
			}
			if (message) {
				showFlag({
					id: ERROR_FLAG_ID,

					type: 'error',
					testId: 'admin-pages-screens-page.ui.contact-admin-form.error',
					title: {
						id: 'admin-pages-contact-administrator.contact-admin-form.server-error-message-could-not-send-mail',
						defaultMessage: message,
						description: 'Mail could not be sent',
					},
				});
			}
			if (errorMessages.length >= 1 || message) {
				return;
			}
		}
		showFlag({
			id: ERROR_FLAG_ID,
			type: 'error',
			testId: 'admin-pages-screens-page.ui.contact-admin-form.error',
			title: messages.errorMessageCouldNotSendMail,
		});
	}, [showFlag, postApiError, reset, shouldRecaptchaWork]);
	const onBack = useCallback(() => {
		// TODO: add analytics
		push('/');
	}, [push]);
	// @ts-expect-error - TS1064 - The return type of an async function or method must be the global Promise<T> type. Did you mean to write 'Promise<undefined>'? | TS2355 - A function whose declared type is neither 'void' nor 'any' must return a value.
	const handleSubmit = async (formData: FormData): Promise<undefined> | undefined => {
		const { requestDetails, fromEmail, subject } = formData;
		const captchaId = captchaToken || '';
		sendMailToAdmin({ requestDetails, fromEmail, subject, captchaId });
	};
	useEffect(() => {
		if (postApiError) {
			onFailure();
		}
	}, [onFailure, postApiError]);
	useEffect(() => {
		if (data === null) {
			onSuccess();
		}
	}, [data, onSuccess]);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const lengthLimit = (length: number) => (value: any) =>
		value.length > length ? formatMessage(messages.errorMessageToLarge) : undefined;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const isValidEmail = (value: any) =>
		!isValidEmailAddress(value) ? formatMessage(messages.errorMessageEmailNotValid) : undefined;

	return (
		// @ts-expect-error - TS2322 - Type '{ children: ({ formProps }: { formProps: FormChildrenProps; disabled: boolean; dirty: boolean; submitting: boolean; getState: () => FormState<FormData, Partial<FormData>>; getValues: () => FormData; setFieldValue: (name: string, value: any) => void; reset: (initialValues?: FormData | undefined) => void; }) => Elemen...' is not assignable to type 'IntrinsicAttributes & FormProps<FormData>'.
		<Form onSubmit={handleSubmit} id={CONTACT_ADMINISTRATOR_ID}>
			{({ formProps }) => (
				<form {...formProps}>
					<Grid spacing="compact">
						<GridColumn medium={8}>
							<FieldWrapper>
								<Field
									label={formatMessage(messages.toLabel)}
									name="to"
									id="reciepient-email"
									defaultValue={to}
								>
									{({ fieldProps, error }) => (
										<>
											<TextField {...fieldProps} isDisabled />
											<ErrorComponent error={error} />
										</>
									)}
								</Field>
							</FieldWrapper>

							<FieldWrapper>
								<Field
									label={formatMessage(messages.fromLabel)}
									name="fromEmail"
									isRequired
									id="sender-email"
									validate={isValidEmail}
									defaultValue={replyTo}
								>
									{({ fieldProps, error }) => (
										<>
											<TextField {...fieldProps} />
											<ErrorComponent error={error} />
										</>
									)}
								</Field>
							</FieldWrapper>

							<FieldWrapper>
								<Field
									label={formatMessage(messages.subjectLabel)}
									name="subject"
									isRequired
									id="mail-subject"
									validate={lengthLimit(SUBJECT_LENGTH)}
									defaultValue=""
								>
									{({ fieldProps, error }) => (
										<>
											<TextField {...fieldProps} />
											<ErrorComponent error={error} />
										</>
									)}
								</Field>
							</FieldWrapper>

							<FieldWrapper>
								<Field
									label={formatMessage(messages.detailsLabel)}
									name="requestDetails"
									isRequired
									id="mail-details"
									validate={lengthLimit(DETAILS_LENGTH)}
									defaultValue=""
								>
									{({ fieldProps, error }) => (
										<>
											{/* @ts-expect-error - TS2322 - Type '{ minimumRows: number; id: string; isRequired: boolean; isDisabled: boolean; isInvalid: boolean; onChange: (value: string | FormEvent<HTMLInputElement>) => void; ... 5 more ...; 'aria-labelledby': string; }' is not assignable to type 'Pick<Combine<Pick<TextareaHTMLAttributes<HTMLTextAreaElement>, "children" | "form" | "slot" | "style" | "title" | "dir" | "name" | "value" | ... 257 more ... | "dirName">, OwnProps>, "children" | ... 277 more ... | "isMonospaced">'. */}
											<TextArea {...fieldProps} minimumRows={10} /> <ErrorComponent error={error} />
										</>
									)}
								</Field>
							</FieldWrapper>
						</GridColumn>
					</Grid>
					{shouldRecaptchaWork && <ButtonContiner>{recaptchaComponent}</ButtonContiner>}
					<ButtonContiner>
						<ButtonWrapper data-test-id="admin-pages-contact-administrator.ui.contact-admin-form.submit-button">
							<LoadingButton
								appearance="primary"
								type="submit"
								isLoading={loading}
								isDisabled={!isSubmitEnabled}
							>
								Submit
							</LoadingButton>
						</ButtonWrapper>
						<ButtonWrapper data-test-id="admin-pages-contact-administrator.ui.contact-admin-form.cancel-button">
							<Button appearance="subtle-link" type="button" onClick={onBack} isDisabled={loading}>
								Cancel
							</Button>
						</ButtonWrapper>
					</ButtonContiner>
				</form>
			)}
		</Form>
	);
};

export default ContactAdminForm;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContiner = styled2.div({
	marginTop: 0,
	marginRight: 0,
	marginBottom: token('space.200'),
	marginLeft: token('space.025'),
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div<{
	children?: ReactNode;
	['data-test-id']: string;
}>({
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldWrapper = styled2.div({
	marginTop: token('space.200'),
	marginRight: 0,
	marginBottom: token('space.200'),
	marginLeft: 0,
});
